/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState, useMemo } from "react";
import floatingWindowCss from "./FloatingWindow.css";
import classNames from "classnames";
import { FloatingWindowProvider, useFloatingWindow } from "./FloatingWindowProvider";
import ButtonWrapper from "dc-preview-utils/src/components/Button/ButtonWrapper";
import Cancel from "dc-icons/access_control/SDC_Close_18_N.svg";
import {
  ANIMATION_NAMES,
  Position,
  StringXPosition,
  StringYPosition,
  TargetRect,
  WindowDimensions
} from "FloatingWindow/IFloatingWindowConstants";
import { getQueryParameterFromUrl } from "dc-preview-utils/src/Utils/UrlUtils";

const FloatingWindowStructure = props => {
  const {
    documentViewPortWidth,
    documentViewPortHeight,
    getWindowPositionToRender,
    getRectsInPreviewViewSpace,
    calculateNewPosition,
    docViewRects,
    careRectBoundary,
    getPositionFromTargetPointInViewSpace,
    id
  } = props;

  const {
    floatingWindowInstance,
    margin,
    moveClientCbRef,
    triggerAnimation,
    cssOverride,
    classNameOverride,
    Component,
    isDraggable,
    showCloseBtn,
    setWindowDimensions
  } = useFloatingWindow();

  const initialCusorRects = useRef(null);
  const [position, setPosition] = useState(null);
  const isDragging = useRef(false);

  const [floatingWindowCurrentHeight, setFloatingWindowCurrentHeight] = useState(0);
  const [floatingWindowCurrentWidth, setFloatingWindowCurrentWidth] = useState(0);

  const floatingWindowRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const previousPageViewRects = useRef(null);

  // Function to recalculate position based on target
  const recalculatePosition = useCallback(() => {
    if (!floatingWindowInstance || !floatingWindowCurrentHeight || !floatingWindowCurrentWidth) return;

    const targetPoint = floatingWindowInstance.targetPoint;
    const targetRect = floatingWindowInstance.targetRect;

    if (targetPoint) {
      const pos = getPositionFromTargetPointInViewSpace(
        targetPoint.x,
        targetPoint.y,
        docViewRects,
        { height: floatingWindowCurrentHeight, width: floatingWindowCurrentWidth },
        margin
      );
      setPosition(pos);
    } else if (targetRect) {
      const targetRectInViewSpace = getRectsInPreviewViewSpace(targetRect);
      const pos = getWindowPositionToRender(
        targetRectInViewSpace,
        { height: floatingWindowCurrentHeight, width: floatingWindowCurrentWidth },
        margin
      );
      setPosition(pos);
    }
  }, [
    floatingWindowInstance,
    floatingWindowCurrentHeight,
    floatingWindowCurrentWidth,
    docViewRects,
    margin,
    getPositionFromTargetPointInViewSpace,
    getRectsInPreviewViewSpace,
    getWindowPositionToRender
  ]);

  // Monitor size changes with ResizeObserver
  useEffect(() => {
    const updateDimensions = () => {
      if (floatingWindowRef.current) {
        const { offsetHeight, offsetWidth } = floatingWindowRef.current;

        if (offsetHeight !== floatingWindowCurrentHeight) {
          setFloatingWindowCurrentHeight(offsetHeight);
        }

        if (offsetWidth !== floatingWindowCurrentWidth) {
          setFloatingWindowCurrentWidth(offsetWidth);
        }
        setWindowDimensions({ width: offsetWidth, height: offsetHeight });
      }
    };

    // Initial dimension check
    updateDimensions();

    // Setup ResizeObserver to monitor size changes
    if (!resizeObserverRef.current && typeof ResizeObserver !== "undefined") {
      resizeObserverRef.current = new ResizeObserver(updateDimensions);

      if (floatingWindowRef.current) {
        resizeObserverRef.current.observe(floatingWindowRef.current);
      }
    }

    return () => {
      resizeObserverRef.current?.disconnect();
    };
  }, []);

  // Recalculate position when dimensions change
  useEffect(() => {
    if (floatingWindowCurrentHeight && floatingWindowCurrentWidth) {
      recalculatePosition();
    }
  }, [floatingWindowCurrentHeight, floatingWindowCurrentWidth]);

  // Initialize pageViewRects tracking page movement while side panels open close
  useLayoutEffect(() => {
    const pageViewRects = document.getElementById("pageview-current-page")?.getBoundingClientRect();
    if (pageViewRects) {
      previousPageViewRects.current = pageViewRects;
    }
  }, []);

  // Setup event listeners
  useLayoutEffect(() => {
    if (!floatingWindowInstance || !floatingWindowCurrentHeight || !floatingWindowCurrentWidth) return;

    recalculatePosition();

    return () => {
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, [floatingWindowInstance, floatingWindowCurrentHeight, floatingWindowCurrentWidth]);

  // Handle document view rect changes // side panels open close
  useLayoutEffect(() => {
    const pageViewRects = document.getElementById("pageview-current-page")?.getBoundingClientRect();
    if (!position || !previousPageViewRects.current) return;

    // Check if pageViewRects has changed
    const pageViewRectsChanged =
      previousPageViewRects.current.left !== pageViewRects.left ||
      previousPageViewRects.current.top !== pageViewRects.top ||
      previousPageViewRects.current.right !== pageViewRects.right ||
      previousPageViewRects.current.bottom !== pageViewRects.bottom;

    let diffX = 0;
    let diffY = 0;

    if (pageViewRects.left !== previousPageViewRects.current.left) {
      diffX = pageViewRects.left - previousPageViewRects.current.left;
    } else if (pageViewRects.right !== previousPageViewRects.current.right) {
      diffX = pageViewRects.right - previousPageViewRects.current.right;
    }

    if (pageViewRects.top !== previousPageViewRects.current.top) {
      diffY = pageViewRects.top - previousPageViewRects.current.top;
    } else if (pageViewRects.bottom !== previousPageViewRects.current.bottom) {
      diffY = pageViewRects.bottom - previousPageViewRects.current.bottom;
    }

    // Update reference
    previousPageViewRects.current = pageViewRects;

    if (!floatingWindowInstance) return;

    const desiredPosition = {
      x: position.x + diffX,
      y: position.y + diffY,
      width: floatingWindowCurrentWidth,
      height: floatingWindowCurrentHeight
    };

    // Check if desired position is within boundaries
    const safeRects = careRectBoundary(desiredPosition, docViewRects);

    if (floatingWindowInstance.targetPoint) {
      floatingWindowInstance.moveTo(safeRects.x, safeRects.y);
    } else {
      const targetRect = floatingWindowInstance.targetRect;
      floatingWindowInstance.moveTo(targetRect.x + diffX, targetRect.y + diffY);
    }

    // Only update position when boundaries are hit, otherwise maintain absolute position
    setPosition({ ...safeRects });
  }, [docViewRects, documentViewPortWidth, documentViewPortHeight, floatingWindowInstance, careRectBoundary]);

  const handleClientMoveTo = useCallback(() => {
    recalculatePosition();
  }, [recalculatePosition]);

  moveClientCbRef.current = handleClientMoveTo;

  const closeBtnHandler = () => {
    floatingWindowInstance?.hide();
  };

  const handleMouseDown = e => {
    initialCusorRects.current = { x: e.clientX, y: e.clientY };
    isDragging.current = true;
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    floatingWindowInstance.dragStart();
  };

  const handleMouseMove = e => {
    if (!floatingWindowInstance || !floatingWindowInstance.draggableState || !isDragging.current) return;

    if (initialCusorRects.current) {
      const newCursorRects = { x: e.clientX, y: e.clientY };
      setPosition(
        prevPosition =>
          prevPosition &&
          calculateNewPosition(initialCusorRects, newCursorRects, prevPosition, {
            height: floatingWindowCurrentHeight,
            width: floatingWindowCurrentWidth
          })
      );
      floatingWindowInstance.drag();
    }
  };

  const handleMouseUp = () => {
    initialCusorRects.current = null;
    isDragging.current = false;
    if (floatingWindowRef.current) {
      const { left, top } = floatingWindowRef.current?.getBoundingClientRect();
      floatingWindowInstance?.moveTo(left, top, true);
    }
    if (floatingWindowInstance?.scrollState) {
      floatingWindowInstance.toggleScroll(() => true); // to reset base scroll point
    }
    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("mouseup", handleMouseUp);
    floatingWindowInstance.dragEnd();
  };

  const animationClass = useMemo(() => {
    switch (triggerAnimation) {
      case ANIMATION_NAMES.SHAKE:
        return floatingWindowCss.shake;
      default:
        return "";
    }
  }, [triggerAnimation]);

  const windowStyle = useMemo(
    () => ({
      opacity: !position ? 0 : 1,
      left: position?.x,
      top: position?.y,
      ...cssOverride
    }),
    [position?.x, position?.y, cssOverride]
  );

  const containerClassName = useMemo(
    () => classNames(floatingWindowCss.windowContainer, animationClass, { [classNameOverride]: classNameOverride }),
    [animationClass, classNameOverride]
  );

  const isDevModeEnabled = getQueryParameterFromUrl(window.location.href, "enableDevMode") === "true";

  const targetRect =
    floatingWindowInstance?.targetRect && getRectsInPreviewViewSpace(floatingWindowInstance.targetRect);

  const getDevModeComponent = () =>
    targetRect && (
      <>
        <div
          style={{
            position: "fixed",
            top: targetRect.y,
            left: targetRect.x,
            width: targetRect.width,
            height: targetRect.height,
            backgroundColor: "red",
            zIndex: 99999,
            opacity: 0.5
          }}
        />
        <div
          style={{
            position: "fixed",
            top: targetRect.y,
            left: 0,
            width: targetRect.x,
            height: 1,
            backgroundColor: "blue",
            zIndex: 99999
          }}
        />
        <div
          style={{
            position: "fixed",
            top: 0,
            left: targetRect.x,
            height: targetRect.y,
            width: 1,
            backgroundColor: "blue",
            zIndex: 99999
          }}
        />
        <p
          style={{
            position: "fixed",
            top: targetRect.y - 60,
            left: targetRect.x - 60,
            zIndex: 99999,
            backgroundColor: "yellow",
            padding: "3px"
          }}
        >
          {targetRect.x}, {targetRect.y}
        </p>
      </>
    );

  return (
    <>
      {isDevModeEnabled && getDevModeComponent()}
      <div
        id={`floating-window-${id}`}
        data-testid={`floating-window-${id}`}
        ref={floatingWindowRef}
        className={containerClassName}
        style={windowStyle}
      >
        {showCloseBtn && (
          <div className={floatingWindowCss.closeBtn}>
            <ButtonWrapper icon={Cancel} clickHandler={closeBtnHandler} ariaLabel="Close" data-testid="closeBtn" />
          </div>
        )}
        {isDraggable && (
          <div
            className={floatingWindowCss.draggableBar}
            data-testid="draggableComponent"
            onMouseDown={handleMouseDown}
          />
        )}
        {React.isValidElement(Component) ? Component : null}
      </div>
    </>
  );
};

const FloatingWindowComponent = props => (
  <FloatingWindowProvider
    id={props.id}
    getPagesViewElementHandle={props.getPagesViewElementHandle}
    docViewRects={props.docViewRects}
  >
    <FloatingWindowStructure {...props} />
  </FloatingWindowProvider>
);

export default FloatingWindowComponent;
